import { Box, FormControl, Input, Typography } from "@mui/joy";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { Util } from "../Helpers/Util";
import WrappedButton from "../WrappedButton";
import PortalTableLayout from "../components/PortalTableLayout";

export interface ProposalMissingElevator {
	InvoiceNumber: string;
	CustomerName: string;
	InvoiceDate: string;
	TotalAmount: number;
	Description: string;
	InvoiceHeaderID: string;
	SerialNumber: string;
}

interface ProposalsMissingElevatorProps {
	embedded?: boolean;
}

export default function ProposalsMissingElevator({ embedded = false }: ProposalsMissingElevatorProps) {
	const [data, setData] = React.useState<Array<ProposalMissingElevator>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(50);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [loadingPdfId, setLoadingPdfId] = React.useState<string | null>(null);
	const [hidingId, setHidingId] = React.useState<string | null>(null);

	React.useEffect(() => {
		getData();
	}, []);

	// Fetches proposals that have a building but no matching elevator
	const getData = () => {
		fetch("Proposal/GetProposalsMissingElevator", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							InvoiceNumber: y.invoice_number,
							CustomerName: y.building_name,
							InvoiceDate: y.invoice_date,
							TotalAmount: y.total_amount,
							Description: y.description,
							InvoiceHeaderID: y.invoice_header_id,
							SerialNumber: y.serial_number,
						} as ProposalMissingElevator;
					})
				);
			});
	};

	// Opens the PDF for the selected proposal
	const openPdf = (proposal: ProposalMissingElevator) => {
		setLoadingPdfId(proposal.InvoiceHeaderID);
		fetch("Proposal/GetPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNum: proposal.InvoiceNumber,
				isPackingList: false,
				invoiceHeaderId: proposal.InvoiceHeaderID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			})
			.finally(() => {
				setLoadingPdfId(null);
			});
	};

	// Hides a proposal from the missing elevator list
	const hideProposal = (proposal: ProposalMissingElevator) => {
		setHidingId(proposal.InvoiceHeaderID);
		fetch("Proposal/HideProposalNoElevator", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				invoiceHeaderId: proposal.InvoiceHeaderID,
			}),
		})
			.then(() => {
				// Refresh the data after hiding
				getData();
			})
			.finally(() => {
				setHidingId(null);
			});
	};

	const filteredData = data?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.CustomerName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter) ||
			x.Description?.toLowerCase().includes(globalFilter.toLowerCase())
	);

	const rowCounts = [5, 10, 25, 50];
	const totalCount = filteredData?.length ?? 0;
	const thisRows = filteredData?.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

	return (
		<>
			<PortalTableLayout
				toolbar={
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: embedded ? "flex-end" : "space-between",
							gap: 1.5,
						}}>
						{!embedded && (
							<Typography level="title-lg">
								Proposals Missing Elevator
							</Typography>
						)}
						<FormControl sx={{ minWidth: 180 }}>
							<Input
								name="Search"
								placeholder="Global Search"
								onChange={(event) => {
									setGlobalFilter(event.target.value);
									setPageNumber(0);
								}}
								value={globalFilter}
							/>
						</FormControl>
					</Box>
				}
				pagination={{
					count: totalCount,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: rowCounts,
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							<TableCell>Estimate #</TableCell>
							<TableCell>Customer Name</TableCell>
							<TableCell>Serial #</TableCell>
							<TableCell>Date</TableCell>
							<TableCell>Amount</TableCell>
							<TableCell>Description</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{thisRows?.map((x) => (
							<TableRow key={x.InvoiceHeaderID} hover sx={{ cursor: "pointer" }}>
								<TableCell>{x.InvoiceNumber}</TableCell>
								<TableCell>{x.CustomerName}</TableCell>
								<TableCell>{x.SerialNumber}</TableCell>
								<TableCell>{new Date(x.InvoiceDate).toLocaleDateString()}</TableCell>
								<TableCell>${x.TotalAmount.toFixed(2)}</TableCell>
								<TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.Description}</TableCell>
								<TableCell>
									<WrappedButton
										busy={loadingPdfId === x.InvoiceHeaderID}
										onClick={() => {
											openPdf(x);
										}}>
										Open PDF
									</WrappedButton>
									<WrappedButton
										busy={hidingId === x.InvoiceHeaderID}
										color="danger"
										variant="soft"
										sx={{ marginLeft: 1 }}
										onClick={() => {
											hideProposal(x);
										}}>
										Hide Proposal
									</WrappedButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
